import { FeedDiagnostics, SiriDiagnosticsPayload } from '../../../shared/models/feed-diagnostics.model';

/** How often the SIRI provider publishes a new diagnostics snapshot. Used only
 *  to flag a snapshot as stale in the UI. Hardcoded because the API doesn't
 *  yet expose the provider's actual poll cadence — swap this for a per-provider
 *  value once that's available. */
export const DIAGNOSTICS_POLL_CYCLE_MINUTES = 10;

export type FeedHealthStatus = 'no-snapshot' | 'failing' | 'not-reporting' | 'ok';

/** Pure, translation-free derivation of a feed's health from its latest SIRI
 *  diagnostics snapshot. Components map `status` (and `error`/`unknown`
 *  counts where needed) to translated labels themselves — keeps this
 *  testable without a TranslocoService in the loop. */
export interface FeedHealth {
  status: FeedHealthStatus;
  total: number;
  ok: number;
  error: number;
  unknown: number;
  arrivals: number;
  isStale: boolean;
}

const EMPTY_HEALTH: FeedHealth = {
  status: 'no-snapshot',
  total: 0,
  ok: 0,
  error: 0,
  unknown: 0,
  arrivals: 0,
  isStale: false,
};

/**
 * Derives feed health from a SIRI diagnostics snapshot. First matching rule
 * wins: no snapshot -> any stop ERROR -> any stop UNKNOWN -> all OK.
 * Only meaningful for `providerType === 'siri'` snapshots — callers must gate
 * on provider type themselves before calling this with a payload they expect
 * to have `stops`.
 */
export function deriveFeedHealth(snapshot: FeedDiagnostics<SiriDiagnosticsPayload> | null): FeedHealth {
  if (!snapshot) return EMPTY_HEALTH;

  const stops = snapshot.payload?.stops ?? [];
  let ok = 0;
  let error = 0;
  let unknown = 0;
  let arrivals = 0;

  for (const stop of stops) {
    if (stop.status === 'OK') ok++;
    else if (stop.status === 'ERROR') error++;
    else unknown++;
    for (const count of Object.values(stop.arrivalsByRoute ?? {})) {
      arrivals += count;
    }
  }

  const status: FeedHealthStatus = error > 0 ? 'failing' : unknown > 0 ? 'not-reporting' : 'ok';

  return { status, total: stops.length, ok, error, unknown, arrivals, isStale: isSnapshotStale(snapshot) };
}

/** A snapshot is stale once older than one poll cycle — independent of
 *  health: a feed can be perfectly green (all stops OK) and still stale if
 *  the provider hasn't reported in a while. */
export function isSnapshotStale(snapshot: FeedDiagnostics<unknown> | null): boolean {
  if (!snapshot) return false;
  const ageMs = Date.now() - new Date(snapshot.generatedAt).getTime();
  return ageMs > DIAGNOSTICS_POLL_CYCLE_MINUTES * 60_000;
}
